import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { vertragAenderungAPI } from '@/lib/api';

/* Vertrag berichtigen (Leitung, 10.10.2026): Tippfehler in Vorname, Nachname oder
   Geburtsdatum am aktiven Vertrag richtigstellen. Kein neuer Vertrag, keine
   Unterschrift — jede Berichtigung steht mit Grund, Name und Datum am Vertrag.
   Mitgliedschaft, Mandat (Erwachsene, die selbst zahlen) und offene
   Rechnungsvorschläge zieht der Server nach. */
const datum = (iso) => iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.${String(iso).slice(0, 4)}` : '–';
const FELD_NAME = { vorname: 'Vorname', nachname: 'Nachname', geburtsdatum: 'Geburtsdatum', full_name: 'Name' };
const norm = (s) => String(s || '').split(/\s+/).join(' ').trim();
const glatt = (s) => norm(s).toLowerCase();
const fehlerText = (e, sonst) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : sonst; };

function Feld({ label, value, onChange, type = 'text', placeholder = '', tid }) {
  return <label className="space-y-1 text-xs"><span className="font-semibold uppercase tracking-wider text-muted-foreground">{label}</span><Input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="h-9" data-testid={tid} /></label>;
}
function Haken({ checked, onChange, children, tid }) {
  return <label className="flex items-start gap-3 cursor-pointer bg-muted/30 rounded-lg p-3 border border-border/60"><input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="accent-marke mt-0.5 w-4 h-4" data-testid={tid} /><span className="text-xs text-muted-foreground">{children}</span></label>;
}

/** Was eine frühere Berichtigung geändert hat, lesbar: „Vorname: „Max“ → „Maxi““ */
function felderText(felder) {
  const alle = Object.entries(felder || {});
  const ohneVoll = alle.filter(([k]) => k !== 'full_name');
  const liste = ohneVoll.length ? ohneVoll : alle;
  if (!liste.length) return 'Mitgliedskonto umbenannt';
  return liste.map(([k, f]) => `${FELD_NAME[k] || k}: „${k === 'geburtsdatum' ? datum(f?.alt) : (f?.alt || '–')}“ → „${k === 'geburtsdatum' ? datum(f?.neu) : (f?.neu || '–')}“`).join(' · ');
}

export default function VertragBerichtigenDialog({ vertragId, offen, onClose, onFertig }) {
  const [vorlage, setVorlage] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!offen || !vertragId) return;
    // Beim Oeffnen zuruecksetzen, bis die frische Vorlage da ist — sonst stuende das Formular des vorigen Vertrags im Dialog
    setVorlage(null); setForm(null);
    vertragAenderungAPI.berichtigungVorlage(vertragId).then(r => {
      const v = r.data; setVorlage(v);
      setForm({ vorname: v.vertrag?.vorname || '', nachname: v.vertrag?.nachname || '', geburtsdatum: v.vertrag?.geburtsdatum || '', grund: '', lead_umbenennen: false });
    }).catch(e => { toast.error(fehlerText(e, 'Vorlage konnte nicht geladen werden')); onClose(); });
  }, [offen, vertragId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!offen || !vorlage || !form) return null;
  const v = vorlage.vertrag || {};
  const lead = vorlage.lead || {};
  const vertragsname = v.full_name || `${v.vorname || ''} ${v.nachname || ''}`.trim();
  // Haken nur, wenn das Mitgliedskonto anders heisst als der Vertrag; traegt es denselben Namen, fuehrt der Server es von selbst mit
  const leadAnders = !!lead.name && glatt(lead.name) !== glatt(vertragsname);
  const mandatMit = v.category !== 'kinder' && v.category !== 'family' && !!v.kontoinhaber && glatt(v.kontoinhaber) === glatt(vertragsname);
  const bisherige = [...(vorlage.berichtigungen || [])].reverse();
  const setzen = (k, w) => setForm(f => ({ ...f, [k]: w }));

  const aenderungen = [
    norm(form.vorname) && norm(form.vorname) !== (v.vorname || '') ? 'Vorname' : null,
    norm(form.nachname) && norm(form.nachname) !== (v.nachname || '') ? 'Nachname' : null,
    form.geburtsdatum && form.geburtsdatum !== (v.geburtsdatum || '') ? 'Geburtsdatum' : null,
  ].filter(Boolean);
  const grund = form.grund.trim();

  const speichern = async () => {
    if (!norm(form.vorname) || !norm(form.nachname)) { toast.error('Vor- und Nachname fehlen'); return; }
    if (form.geburtsdatum && !/^\d{4}-\d{2}-\d{2}$/.test(form.geburtsdatum)) { toast.error('Geburtsdatum vollständig wählen'); return; }
    if (!aenderungen.length && !(leadAnders && form.lead_umbenennen)) { toast.error('Nichts zu berichtigen — die Angaben sind unverändert'); return; }
    if (grund.length < 3 || grund.length > 300) { toast.error('Grund angeben (3 bis 300 Zeichen)'); return; }
    setBusy(true);
    try {
      const r = (await vertragAenderungAPI.berichtigen(vertragId, {
        vorname: norm(form.vorname), nachname: norm(form.nachname), geburtsdatum: form.geburtsdatum || null,
        grund, lead_umbenennen: leadAnders && !!form.lead_umbenennen,
      })).data;
      const was = Object.keys(r.felder || {}).filter(k => k !== 'full_name').map(k => FELD_NAME[k] || k);
      toast.success(`Vertrag ${r.vertrag?.vertragsnummer || ''} berichtigt${was.length ? `: ${was.join(', ')}` : ''}${r.lead_umbenannt ? ' · Mitgliedskonto umbenannt' : ''}`);
      (r.hinweise || []).forEach(h => toast.info(h, { duration: 8000 }));
      onFertig?.(r.vertrag);
    } catch (e) { toast.error(fehlerText(e, 'Berichtigung fehlgeschlagen')); }
    setBusy(false);
  };

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="berichtigen-dialog">
        <DialogHeader><DialogTitle>Vertrag berichtigen · {v.vertragsnummer}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs space-y-0.5">
            <p><b>{vertragsname}</b>{v.mitgliedsnummer ? ` · Mitgliedsnr. ${v.mitgliedsnummer}` : ''}{v.plan_name ? ` · ${v.plan_name}` : ''}</p>
            <p className="text-muted-foreground">Geburtsdatum {datum(v.geburtsdatum)} · Kontoinhaber/in {v.kontoinhaber || '–'}{v.erziehungsberechtigter_name ? ` · Erziehungsberechtigte/r ${v.erziehungsberechtigter_name}` : ''}</p>
            <p className="text-muted-foreground">Laufzeit, Beitrag, Mandat und Unterschriften bleiben. Jede Berichtigung steht mit Grund, Name und Datum am Vertrag.{mandatMit ? ' Das SEPA-Mandat lautet auf das Mitglied und wird bei einer Namensänderung mitgeführt.' : ''}</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Feld label="Vorname" value={form.vorname} onChange={w => setzen('vorname', w)} tid="berichtigen-vorname" />
            <Feld label="Nachname" value={form.nachname} onChange={w => setzen('nachname', w)} tid="berichtigen-nachname" />
            <Feld label="Geburtsdatum" type="date" value={form.geburtsdatum} onChange={w => setzen('geburtsdatum', w)} tid="berichtigen-geburtsdatum" />
          </div>
          <label className="space-y-1 text-xs block">
            <span className="font-semibold uppercase tracking-wider text-muted-foreground">Grund (Pflicht)</span>
            <Textarea value={form.grund} onChange={e => setzen('grund', e.target.value)} placeholder="z. B. Tippfehler im Nachnamen, laut Ausweis" rows={2} maxLength={300} data-testid="berichtigen-grund" />
          </label>
          {leadAnders ? (
            <Haken checked={form.lead_umbenennen} onChange={w => setzen('lead_umbenennen', w)} tid="berichtigen-lead-umbenennen">
              Mitgliedskonto ebenfalls umbenennen. Das Mitgliedskonto heißt derzeit ‚{lead.name}‘.
            </Haken>
          ) : lead.name ? (
            <p className="text-xs text-muted-foreground">Das Mitgliedskonto trägt denselben Namen und wird bei einer Namensänderung mitgeführt; ein berichtigtes Geburtsdatum wird dort ebenfalls eingetragen.</p>
          ) : null}
          {bisherige.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bisherige Berichtigungen</p>
              <ul className="space-y-1 text-xs" data-testid="berichtigen-historie">
                {bisherige.map((b, i) => (
                  <li key={b.id || i} className="rounded-lg border border-border/60 px-3 py-2">
                    <p><span className="font-medium">{datum(b.am)}</span> · {b.von_name || 'unbekannt'}</p>
                    <p className="text-muted-foreground">{felderText(b.felder)}{b.lead_umbenannt ? ' · Mitgliedskonto umbenannt' : ''}</p>
                    {b.grund && <p className="text-muted-foreground italic">{b.grund}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button size="sm" onClick={speichern} disabled={busy} className="gap-1.5" data-testid="berichtigen-speichern">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />} Berichtigung speichern</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
